"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { cicToType } from "@/helpers/Helpers";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import UserCICAndLevel, { UserCICAndLevelSize } from "@/components/user/utils/UserCICAndLevel";
import { ApiDrop } from "@/generated/models/ApiDrop";
import WaveDropTime from "./time/WaveDropTime";
import UserProfileTooltipWrapper from "@/components/utils/tooltip/UserProfileTooltipWrapper";
import { ArtistSubmissionBadge } from "./ArtistSubmissionBadge";
import { ArtistPreviewModal } from "./ArtistPreviewModal";
import { ProfileWinnerBadge } from "./ProfileWinnerBadge";
import { useMemo, useCallback } from "react";
import { useArtistPreviewModal } from "@/hooks/useArtistPreviewModal";
interface WaveDropHeaderProps {
  readonly drop: ApiDrop;
  readonly isStorm: boolean;
  readonly currentPartIndex: number;
  readonly partsCount: number;
  readonly showWaveInfo: boolean;
  readonly badge?: React.ReactNode;
}
const WaveDropHeader: React.FC<WaveDropHeaderProps> = t0 => {
  const $ = _c(61);
  const {
    drop,
    isStorm,
    currentPartIndex,
    partsCount,
    showWaveInfo,
    badge
  } = t0;
  const router = useRouter();
  const {
    isModalOpen,
    modalInitialTab,
    handleBadgeClick,
    handleModalClose
  } = useArtistPreviewModal();
  let t1;
  if ($[0] !== drop.author.cic) {
    t1 = cicToType(drop.author.cic);
    $[0] = drop.author.cic;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const cicType = t1;
  const submissionCount = drop.author.active_main_stage_submission_ids?.length || 0;
  const hasSubmissions = submissionCount > 0;
  const winnerCount = drop.author.winner_main_stage_drop_ids?.length || 0;
  const isWinner = winnerCount > 0;
  let t2;
  if ($[2] !== router) {
    t2 = (e, path) => {
      e.preventDefault();
      e.stopPropagation();
      router.push(path);
    };
    $[2] = router;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleNavigation = t2;
  let t3;
  if ($[4] !== cicType || $[5] !== drop.author.level) {
    t3 = <UserCICAndLevel level={drop.author.level} cicType={cicType} size={UserCICAndLevelSize.SMALL} />;
    $[4] = cicType;
    $[5] = drop.author.level;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = drop.author.handle ?? drop.author.id;
  let t5;
  if ($[7] !== drop.author.handle || $[8] !== handleNavigation) {
    t5 = e_0 => handleNavigation(e_0, `/${drop.author.handle}`);
    $[7] = drop.author.handle;
    $[8] = handleNavigation;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = `/${drop.author.handle}`;
  let t7;
  if ($[10] !== drop.author.handle || $[11] !== t5 || $[12] !== t6) {
    t7 = <Link onClick={t5} href={t6} className="tw-no-underline desktop-hover:hover:tw-underline tw-text-iron-200 desktop-hover:hover:tw-text-opacity-80 tw-transition tw-duration-300 tw-ease-out">{drop.author.handle}</Link>;
    $[10] = drop.author.handle;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t4 || $[15] !== t7) {
    t8 = <p className="tw-text-md tw-mb-0 tw-leading-none tw-font-semibold"><UserProfileTooltipWrapper user={t4}>{t7}</UserProfileTooltipWrapper></p>;
    $[14] = t4;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== drop.id || $[18] !== handleBadgeClick || $[19] !== hasSubmissions || $[20] !== submissionCount) {
    t9 = hasSubmissions && <ArtistSubmissionBadge submissionCount={submissionCount} onBadgeClick={() => handleBadgeClick("active")} tooltipId={`header-badge-${drop.id}`} />;
    $[17] = drop.id;
    $[18] = handleBadgeClick;
    $[19] = hasSubmissions;
    $[20] = submissionCount;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== drop.id || $[23] !== handleBadgeClick || $[24] !== isWinner || $[25] !== winnerCount) {
    t10 = isWinner && <ProfileWinnerBadge winCount={winnerCount} onBadgeClick={() => handleBadgeClick("winners")} tooltipId={`winner-badge-${drop.id}`} />;
    $[22] = drop.id;
    $[23] = handleBadgeClick;
    $[24] = isWinner;
    $[25] = winnerCount;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="tw-size-[3px] tw-bg-iron-600 tw-rounded-full tw-flex-shrink-0" />;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== drop.created_at) {
    t12 = <WaveDropTime timestamp={drop.created_at} />;
    $[28] = drop.created_at;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t10 || $[31] !== t12 || $[32] !== t3 || $[33] !== t8 || $[34] !== t9) {
    t13 = <div className="tw-flex tw-items-center tw-gap-x-2">{t3}{t8}{t9}{t10}{t11}{t12}</div>;
    $[30] = t10;
    $[31] = t12;
    $[32] = t3;
    $[33] = t8;
    $[34] = t9;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== badge) {
    t14 = badge && <div className="tw-ml-2">{badge}</div>;
    $[36] = badge;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== t13 || $[39] !== t14) {
    t15 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-x-2"><div className="tw-flex tw-items-center tw-gap-x-2">{t13}{t14}</div></div>;
    $[38] = t13;
    $[39] = t14;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== drop.wave || $[42] !== handleNavigation || $[43] !== showWaveInfo) {
    t16 = showWaveInfo && drop.wave && (() => {
      const waveMeta = (drop.wave as unknown as {
        chat?: {
          scope?: {
            group?: {
              is_direct_message?: boolean;
            };
          };
        };
      })?.chat;
      const waveHref = getWaveRoute({
        waveId: drop.wave.id,
        isDirectMessage: waveMeta?.scope?.group?.is_direct_message ?? false,
        isApp: false
      });
      return <Link onClick={e_1 => handleNavigation(e_1, waveHref)} href={waveHref} className="tw-mb-0 tw-text-[11px] tw-leading-0 -tw-mt-1 tw-text-iron-500 hover:tw-text-iron-300 tw-transition tw-duration-300 tw-ease-out tw-no-underline">{drop.wave.name}</Link>;
    })();
    $[41] = drop.wave;
    $[42] = handleNavigation;
    $[43] = showWaveInfo;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== t16) {
    t17 = <div>{t16}</div>;
    $[45] = t16;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== currentPartIndex || $[48] !== isStorm || $[49] !== partsCount) {
    t18 = isStorm && <div className="tw-inline-flex tw-relative tw-mt-2"><span className="tw-text-xs tw-text-iron-50 tw-mb-1.5">{currentPartIndex + 1} /{" "}<span className="tw-text-iron-400">{partsCount}</span></span></div>;
    $[47] = currentPartIndex;
    $[48] = isStorm;
    $[49] = partsCount;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  let t19;
  if ($[51] !== drop.author || $[52] !== handleModalClose || $[53] !== isModalOpen || $[54] !== modalInitialTab) {
    t19 = <ArtistPreviewModal isOpen={isModalOpen} onClose={handleModalClose} user={drop.author} initialTab={modalInitialTab} />;
    $[51] = drop.author;
    $[52] = handleModalClose;
    $[53] = isModalOpen;
    $[54] = modalInitialTab;
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  let t20;
  if ($[56] !== t15 || $[57] !== t17 || $[58] !== t18 || $[59] !== t19) {
    t20 = <>{t15}{t17}{t18}{t19}</>;
    $[56] = t15;
    $[57] = t17;
    $[58] = t18;
    $[59] = t19;
    $[60] = t20;
  } else {
    t20 = $[60];
  }
  return t20;
};
export default WaveDropHeader;